import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  message,
  DatePicker,
  Select,
  Typography,
  Space,
  Divider,
  Tag,
  Row,
  Col,
  Tooltip,
  Badge
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  DownOutlined
} from '@ant-design/icons';
import InputNumber from 'antd/es/input-number';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import useDocumentGeneration from '../../hooks/useDocumentGeneration';
import { colors, spacing, radius, typography } from '../../styles/monochromeTheme';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Menu data structure
const MENUS = {
  'Menu 75': {
    price: '75€',
    sections: [
      { name: 'APÉRITIF', type: 'single', items: [
        { id: 'ap-75-1', name: 'Houmous, courgettes vinaigrette sureaux & citron' }
      ]},
      { name: 'ENTRÉE', type: 'choice', items: [
        { id: 'en-75-1', name: 'Sashimi de poisson du jour, sauce sésame wasabi' },
        { id: 'en-75-2', name: 'Courgette & feta, vinaigrette basilic & sureaux, cacahuètes & amandes' }
      ]},
      { name: 'PLAT', type: 'choice', items: [
        { id: 'pl-75-1', name: 'Pêche du jour, vinaigrette tomate avec riz et légumes' },
        { id: 'pl-75-2', name: 'Brochette de volaille, sauce mojo verde, riz et légumes' }
      ]},
      { name: 'DESSERT', type: 'choice', items: [
        { id: 'de-75-1', name: 'Tartelette chocolat 32%, ganache chocolat lait, chantilly vanille, caramel beurre salé' },
        { id: 'de-75-2', name: 'Tartelette citron, crème vanille, zest citron vert' }
      ]}
    ]
  },
  'Menu 95': {
    price: '95€',
    sections: [
      { name: 'APÉRITIF', type: 'single', items: [
        { id: 'ap-95-1', name: 'Houmous, vinaigrette basilic sureaux & citron' }
      ]},
      { name: 'ENTRÉE', type: 'choice', items: [
        { id: 'en-95-1', name: "Salade d'artichauts, salade verte vinaigrette truffe & balsamique, noisettes caramélisées" },
        { id: 'en-95-2', name: 'Sashimi de thon, ponzu à la fleur de cerisier' }
      ]},
      { name: 'PLAT', type: 'choice', items: [
        { id: 'pl-95-1', name: 'Pêche du jour, vinaigrette aji amarillo & citron avec riz & légumes' },
        { id: 'pl-95-2', name: 'Carpaccio de boeuf, sauce césar, parmesan, ciboulette & gel romarin avec riz & légumes' },
        { id: 'pl-95-3', name: 'Brochette de volaille, sauce mojo verde avec riz et légumes' }
      ]},
      { name: 'DESSERT', type: 'choice', items: [
        { id: 'de-95-1', name: 'Tartelette chocolat opera 32%, ganache, vanille, caramel beurre salé' },
        { id: 'de-95-2', name: 'Tartelette citron, crème vanillée, zest citron vert' }
      ]}
    ]
  },
  'Balade Gourmande': {
    price: '80€',
    sections: [
      { name: 'SNACKS', type: 'single', items: [
        { id: 'sn-bg-1', name: 'Quiche du jour avec salade artisanale & Parmesan' },
        { id: 'sn-bg-2', name: 'Chips de pomme de terre & betterave' },
        { id: 'sn-bg-3', name: 'Dips de légumes' }
      ]},
      { name: 'SALADES', type: 'choice', items: [
        { id: 'sa-bg-1', name: 'Salade de pois : Pois, butternut grillé, chèvre frais, noisettes caramélisées, citron' },
        { id: 'sa-bg-2', name: 'Burrata – Tomate : mélange de tomates, Buratta 125g, vinaigrette' }
      ]},
      { name: 'PLAT PRINCIPAL', type: 'choice', items: [
        { id: 'mc-bg-1', name: "Brochettes de poulet marinées" },
        { id: 'mc-bg-2', name: "Brochettes de poisson local mariné" }
      ]},
      { name: 'ACCOMPAGNEMENTS', type: 'single', items: [
        { id: 'si-bg-1', name: 'Légumes grillés & salade asiatique' },
        { id: 'si-bg-2', name: 'Pain maison' }
      ]},
      { name: 'DESSERT', type: 'single', items: [
        { id: 'ds-bg-1', name: 'Fruits frais & Cookie' }
      ]}
    ]
  },
  'Évasion Dorée': {
    price: '110€',
    sections: [
      { name: 'SNACKS', type: 'single', items: [
        { id: 'sn-ed-1', name: 'Bagel saumon fumé & cream cheese' },
        { id: 'sn-ed-2', name: 'Verrine de salade de quinoa' },
        { id: 'sn-ed-3', name: 'Verrine de poisson crudo' },
        { id: 'sn-ed-4', name: 'Mini Quiche' }
      ]},
      { name: 'SALADES', type: 'choice', items: [
        { id: 'sa-ed-1', name: 'Niçoise : Salade, tomate, thon confit, œuf dur, anchois, olive, poivron, radis, céleri, oignon rouge' },
        { id: 'sa-ed-2', name: 'Burrata – Tomate : Mélange de tomates, Buratta 125g, vinaigrette' }
      ]},
      { name: 'PLAT PRINCIPAL', type: 'choice', items: [
        { id: 'mc-ed-1', name: "Brochettes de bœuf mariné" },
        { id: 'mc-ed-2', name: 'Tataki de thon' }
      ]},
      { name: 'ACCOMPAGNEMENTS', type: 'single', items: [
        { id: 'si-ed-1', name: 'Sélection de fromages' },
        { id: 'si-ed-2', name: 'Légumes grillés & salade de riz' },
        { id: 'si-ed-3', name: 'Pain maison' }
      ]},
      { name: 'DESSERT', type: 'single', items: [
        { id: 'ds-ed-1', name: 'Tartelette aux fruits rouges & Cookie' }
      ]}
    ]
  },
  'The Lovely Cruise Menu': {
    price: '75€',
    sections: [
      { name: 'STARTERS', type: 'single', items: [
        { id: 'bb-lc-1', name: 'Ribeye Ham — Beef prosciutto, Carasau crackers' },
        { id: 'bb-lc-2', name: 'Avocado Houmous — Avocado, tahini, chips' }
      ]},
      { name: 'SALADS', type: 'single', items: [
        { id: 'bb-lc-3', name: 'Super Kale — Avocado, Parmesan cheese, cherry tomatoes, lemon zests' }
      ]},
      { name: 'DESSERTS', type: 'single', items: [
        { id: 'bb-lc-4', name: 'Chocolate Chip Cookies' },
        { id: 'bb-lc-5', name: 'Fresh Fruits' }
      ]}
    ]
  },
  'The Awesome Cruise Menu': {
    price: '110€',
    sections: [
      { name: 'STARTERS', type: 'single', items: [
        { id: 'bb-ac-1', name: 'Ribeye Ham — Beef prosciutto, Carasau crackers' },
        { id: 'bb-ac-2', name: 'Avocado Houmous — Avocado, tahini, chips' }
      ]},
      { name: 'SALADS', type: 'single', items: [
        { id: 'bb-ac-3', name: 'Super Kale — Avocado, Parmesan cheese, cherry tomatoes, lemon zests' },
        { id: 'bb-ac-4', name: 'Ceviche Rosado — Mahi-mahi ceviche, sweet potato, avocado, raspberries, truffle oil, quinoa' },
        { id: 'bb-ac-5', name: 'Thai Beef Salad — Marinated beef fillet, green mango, coriander' }
      ]},
      { name: 'DESSERTS', type: 'single', items: [
        { id: 'bb-ac-6', name: 'Tiramisu' },
        { id: 'bb-ac-7', name: 'Chocolate Chip Cookies' },
        { id: 'bb-ac-8', name: 'Fresh Fruits' }
      ]}
    ]
  },
  'The Ultimate Cruise Menu': {
    price: '190€',
    sections: [
      { name: 'STARTERS', type: 'single', items: [
        { id: 'bb-uc-1', name: 'Ribeye Ham — Beef prosciutto & Carasau crackers' },
        { id: 'bb-uc-2', name: 'Avocado Houmous — Avocado, tahini, chips' }
      ]},
      { name: 'SALADS', type: 'single', items: [
        { id: 'bb-uc-3', name: 'Umami Caesar Salad — Romaine salad, Caesar miso sauce, Parmesan cheese, Kobe Kasarumi' },
        { id: 'bb-uc-4', name: 'Ceviche Rosado — Mahi-mahi ceviche, sweet potato, avocado, raspberries, truffle oil, quinoa' },
        { id: 'bb-uc-5', name: 'Tuna Tiradito — Tuna, avocado, grapefruit, miso, lime' },
        { id: 'bb-uc-6', name: 'Super Kale — Avocado, Parmesan cheese, cherry tomatoes, lemon zests' },
        { id: 'bb-uc-7', name: 'Thai Beef Salad — Marinated beef fillet, green mango, coriander' }
      ]},
      { name: 'DESSERTS', type: 'single', items: [
        { id: 'bb-uc-8', name: 'Fresh Fruits' },
        { id: 'bb-uc-9', name: 'Tiramisu' },
        { id: 'bb-uc-10', name: 'Beefbar Magic Fingers — Roasted pistachios, caramel, crispy kadaif, 40% milk chocolate and a touch of white chocolate' }
      ]}
    ]
  }
};

const RESTAURANT_GROUPS = {
  mapo: {
    label: 'Mapo',
    icon: '🍽️',
    subtitle: 'Menu 75€ · Menu 95€',
    menus: ['Menu 75', 'Menu 95'],
  },
  eden: {
    label: 'Eden to Go',
    icon: '🌿',
    subtitle: 'Balade Gourmande · Évasion Dorée',
    menus: ['Balade Gourmande', 'Évasion Dorée'],
  },
  beefbar: {
    label: 'Beefbar',
    icon: '🥩',
    subtitle: 'Lovely · Awesome · Ultimate',
    menus: ['The Lovely Cruise Menu', 'The Awesome Cruise Menu', 'The Ultimate Cruise Menu'],
  },
};

// Monochrome styles
const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xxl,
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxl,
  },
  menuCard: {
    textAlign: 'center',
    borderRadius: radius.xl,
    border: `1px solid ${colors.borderMedium}`,
    padding: spacing.lg,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  menuCardActive: {
    borderColor: colors.bgDark,
    borderWidth: '2px',
    background: colors.bgTertiary,
  },
  variationCard: {
    marginBottom: spacing.md,
    backgroundColor: colors.bgTertiary,
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderSubtle}`,
    padding: spacing.lg,
  },
  itemCard: {
    border: `1px solid ${colors.borderMedium}`,
    borderRadius: radius.md,
    padding: spacing.sm,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  itemCardSelected: {
    border: `2px solid ${colors.bgDark}`,
    background: colors.bgTertiary,
  },
  itemCardIncluded: {
    border: `2px solid ${colors.accentSuccess}`,
    background: colors.accentSuccessBg,
  },
  primaryBtn: {
    height: '52px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  secondaryBtn: {
    height: '44px',
    borderRadius: radius.lg,
    background: colors.bgSecondary,
    border: `1px solid ${colors.borderMedium}`,
    color: colors.textSecondary,
  },
  infoBox: {
    padding: spacing.lg,
    backgroundColor: colors.bgTertiary,
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderSubtle}`,
  },
};

function LunchDocumentGenerator() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedMenus, setSelectedMenus] = useState({});
  const [expandedInstances, setExpandedInstances] = useState({});
  const [viewMode, setViewMode] = useState('compact');
  const { generateDoc, loading } = useDocumentGeneration();

  const visibleMenus = selectedRestaurant
    ? RESTAURANT_GROUPS[selectedRestaurant].menus
    : [];

  const handleSelectRestaurant = (key) => {
    if (selectedRestaurant !== key) {
      setSelectedMenus({});
    }
    setSelectedRestaurant(key);
  };

  const formatAllMenuSelections = () => {
    let details = '';
    let totalIndex = 0;

    Object.entries(selectedMenus).forEach(([menuName, instances]) => {
      const menu = MENUS[menuName];
      if (!menu || instances.length === 0) return;

      instances.forEach((instance, instanceIndex) => {
        const quantity = instance.quantity || 1;
        if (totalIndex > 0) details += '\n\n─────────────────────────────────\n\n';
        const quantityText = quantity > 1 ? ` x${quantity}` : '';
        details += `${menuName} (${menu.price})${quantityText} - Variation ${instanceIndex + 1}\n\n`;

        menu.sections.forEach(section => {
          const selectedItem = instance.selections[section.name];
          if (selectedItem) {
            details += `${section.name}: ${selectedItem.name}\n`;
            if (selectedItem.nameEn) details += `  (${selectedItem.nameEn})\n`;
          } else if (section.type === 'single' && section.items.length > 0) {
            section.items.forEach(item => {
              details += `${section.name}: ${item.name}\n`;
              if (item.nameEn) details += `  (${item.nameEn})\n`;
            });
          }
        });
        totalIndex++;
      });
    });
    return details.trim();
  };

  useEffect(() => {
    const formattedDetails = formatAllMenuSelections();
    form.setFieldsValue({ reservation_details: formattedDetails });
  }, [selectedMenus, form]);

  const handleAddMenuInstance = (menuName) => {
    setSelectedMenus(prev => {
      const newMenus = { ...prev };
      const instances = newMenus[menuName] || [];
      const newId = instances.length > 0 ? Math.max(...instances.map(i => i.id)) + 1 : 1;
      newMenus[menuName] = [...instances, { id: newId, selections: {}, quantity: 1 }];
      return newMenus;
    });
  };

  const handleQuantityChange = (menuName, instanceId, newQuantity) => {
    if (newQuantity < 1) return;
    setSelectedMenus(prev => {
      const newMenus = { ...prev };
      const instances = newMenus[menuName] || [];
      const updatedInstances = instances.map(inst => inst.id === instanceId ? { ...inst, quantity: newQuantity } : inst);
      newMenus[menuName] = updatedInstances;
      return newMenus;
    });
  };

  const handleRemoveMenuInstance = (menuName, instanceId) => {
    setSelectedMenus(prev => {
      const newMenus = { ...prev };
      const instances = newMenus[menuName] || [];
      newMenus[menuName] = instances.filter(inst => inst.id !== instanceId);
      if (newMenus[menuName].length === 0) delete newMenus[menuName];
      return newMenus;
    });
  };

  const handleItemSelect = (menuName, instanceId, sectionName, item) => {
    setSelectedMenus(prev => {
      const newMenus = { ...prev };
      const instances = newMenus[menuName] || [];
      const updatedInstances = instances.map(inst => {
        if (inst.id === instanceId) {
          const currentSelections = { ...inst.selections };
          if (currentSelections[sectionName]?.id === item.id) delete currentSelections[sectionName];
          else currentSelections[sectionName] = item;
          return { ...inst, selections: currentSelections };
        }
        return inst;
      });
      newMenus[menuName] = updatedInstances;
      return newMenus;
    });
  };

  const handleCopySelections = (menuName, fromInstanceId, toInstanceId) => {
    setSelectedMenus(prev => {
      const newMenus = { ...prev };
      const instances = newMenus[menuName] || [];
      const fromInstance = instances.find(inst => inst.id === fromInstanceId);
      if (!fromInstance) return prev;
      const updatedInstances = instances.map(inst => inst.id === toInstanceId ? { ...inst, selections: { ...fromInstance.selections } } : inst);
      newMenus[menuName] = updatedInstances;
      return newMenus;
    });
    message.success('Sélections copiées !');
  };

  const getInstanceSummary = (menuName, instance) => {
    const menu = MENUS[menuName];
    if (!menu) return 'Aucune sélection';
    const choiceSections = menu.sections.filter(s => s.type === 'choice');
    const selectedCount = choiceSections.filter(s => instance.selections[s.name]).length;
    if (selectedCount === 0) return 'Aucun choix effectué';
    if (selectedCount === choiceSections.length) return 'Tous les choix effectués';
    return `${selectedCount}/${choiceSections.length} choix effectués`;
  };

  const isInstanceComplete = (menuName, instance) => {
    const menu = MENUS[menuName];
    if (!menu) return false;
    const choiceSections = menu.sections.filter(s => s.type === 'choice');
    return choiceSections.every(s => instance.selections[s.name]);
  };

  const toggleInstanceExpansion = (menuName, instanceId) => {
    const key = `${menuName}-${instanceId}`;
    setExpandedInstances(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isInstanceExpanded = (menuName, instanceId) => expandedInstances[`${menuName}-${instanceId}`] || false;

  const getTotalPeopleCount = () => Object.values(selectedMenus).reduce((total, instances) => total + instances.reduce((sum, inst) => sum + (inst.quantity || 1), 0), 0);

  const handleSubmit = async (values) => {
    try {
      const charterId = `LUNCH-${Date.now()}`;
      const todayDate = dayjs().format('DD/MM/YYYY');

      let menuText = '';
      let menuPriceTotal = 0;
      let totalMenuCount = 0;

      Object.entries(selectedMenus).forEach(([menuName, instances]) => {
        const menu = MENUS[menuName];
        if (!menu || instances.length === 0) return;

        const pricePerMenu = parseFloat(menu.price.replace('€', '')) || 0;

        // Grouper les variations identiques
        const variantGroups = new Map();
        instances.forEach((instance) => {
          const quantity = instance.quantity || 1;
          const choicesKey = menu.sections.map(section => {
            const item = instance.selections[section.name];
            return item ? `${section.name}:${item.id}` : '';
          }).filter(Boolean).join('|');

          if (variantGroups.has(choicesKey)) {
            variantGroups.get(choicesKey).totalQuantity += quantity;
          } else {
            variantGroups.set(choicesKey, { instance, totalQuantity: quantity });
          }
        });

        // Calculer le nombre total de menus pour ce type
        let menuTypeCount = 0;
        variantGroups.forEach(({ totalQuantity }) => {
          menuTypeCount += totalQuantity;
        });
        totalMenuCount += menuTypeCount;

        // Ajouter l'en-tête du menu avec prix unitaire et quantité totale
        if (menuText) menuText += '\n\n─────────────────────────────────\n\n';
        menuText += `${menuName}\n`;
        menuText += `Prix unitaire: ${menu.price}\n`;
        menuText += `Quantité: ${menuTypeCount}\n`;

        // Calculer le sous-total pour ce type de menu
        const menuTypeTotal = pricePerMenu * menuTypeCount;
        menuPriceTotal += menuTypeTotal;

        // Sections à afficher de façon simplifiée : Entrée/Salade, Plat, Dessert
        const simplifiedSections = ['ENTRÉE', 'SALADES', 'PLAT', 'PLAT PRINCIPAL', 'DESSERT'];

        // Afficher les variations seulement si plusieurs variations différentes
        if (variantGroups.size > 1) {
          let variantIndex = 0;
          variantGroups.forEach(({ instance, totalQuantity }) => {
            variantIndex++;
            menuText += `\n  Variation ${variantIndex} (x${totalQuantity}):\n`;

            menu.sections.forEach(section => {
              const selectedItem = instance.selections[section.name];
              // Afficher seulement les sections simplifiées (Entrée/Salade, Plat, Dessert)
              if (simplifiedSections.includes(section.name) && selectedItem) {
                menuText += `    ${section.name}: ${selectedItem.name}\n`;
              }
            });
          });
        } else {
          // Une seule variation - afficher les choix directement
          const { instance } = variantGroups.values().next().value;
          menuText += '\n';
          menu.sections.forEach(section => {
            const selectedItem = instance.selections[section.name];
            // Afficher seulement les sections simplifiées (Entrée/Salade, Plat, Dessert)
            if (simplifiedSections.includes(section.name) && selectedItem) {
              menuText += `${section.name}: ${selectedItem.name}\n`;
            }
          });
        }
      });

      const additionalText = values.additional_items || '';
      const additionalPrice = parseFloat(values.additional_price) || 0;
      const totalPrice = menuPriceTotal + additionalPrice;

      const documentData = {
        today_date: todayDate,
        charter_date: values.charter_date.format('DD/MM/YYYY'),
        client_name: values.client_name,
        invoice_number: values.invoice_number || '',
        menu_text: menuText,
        menu_price: `${menuPriceTotal}€`,
        additional_text: additionalText,
        additional_price: additionalPrice > 0 ? `${additionalPrice}€` : '',
        total_price: `${totalPrice}€`,
        reservation_details: formatAllMenuSelections()
      };

      await generateDoc(values.document_type, documentData, charterId, 'lunch');
      form.resetFields();
      setSelectedMenus({});
      setSelectedRestaurant(null);
    } catch (error) {
      console.error('Error:', error);
      let errorMessage = 'Erreur lors de la génération du document';
      if (error && error.message) errorMessage += ': ' + error.message;
      message.error(errorMessage);
    }
  };

  return (
    <div style={styles.page}>
      <style>
        {`
          .ant-form-item-label > label { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textSecondary} !important; text-transform: uppercase; letter-spacing: 0.5px; }
          .ant-input, .ant-select-selector, .ant-picker, .ant-input-number { border-radius: ${radius.md} !important; border-color: ${colors.borderMedium} !important; }
          .ant-input:focus, .ant-select-selector:focus, .ant-picker-focused { border-color: ${colors.bgDark} !important; box-shadow: 0 0 0 2px rgba(0,0,0,0.1) !important; }
          .ant-divider-inner-text { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textMuted} !important; text-transform: uppercase; letter-spacing: 1px; }
        `}
      </style>

      <div style={styles.container}>
        <div style={styles.card}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ document_type: 'Contrat', charter_date: dayjs() }}>
              <Form.Item name="document_type" label="Document Type" rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}>
                <Select size="large">
                  <Option value="Contrat">Contrat Lunch</Option>
                  <Option value="Confirmation">Confirmation Lunch</Option>
                  <Option value="Facture">Facture Lunch</Option>
                </Select>
              </Form.Item>

              <Form.Item name="charter_date" label="Reservation Date" rules={[{ required: true, message: 'Requis' }]}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} size="large" />
              </Form.Item>

              <Form.Item name="client_name" label="Client Name" rules={[{ required: true, message: 'Requis' }]}>
                <Input size="large" placeholder="John Smith" />
              </Form.Item>

              <Divider>
                <Space>
                  Menu Selection
                  {getTotalPeopleCount() > 0 && <Badge count={getTotalPeopleCount()} style={{ backgroundColor: colors.bgDark }} />}
                </Space>
              </Divider>

              {/* Pre-step: Restaurant choice */}
              <Text type="secondary" style={{ display: 'block', marginBottom: '12px', fontSize: typography.fontSize.sm }}>
                Step 1: Choose a restaurant
              </Text>

              <Row gutter={[12, 12]} style={{ marginBottom: '24px' }}>
                {Object.entries(RESTAURANT_GROUPS).map(([key, restaurant]) => {
                  const isActive = selectedRestaurant === key;
                  return (
                    <Col xs={24} sm={8} key={key}>
                      <div
                        onClick={() => handleSelectRestaurant(key)}
                        style={{
                          padding: '16px',
                          borderRadius: radius.xl,
                          border: isActive ? `2px solid ${colors.bgDark}` : `1px solid ${colors.borderMedium}`,
                          background: isActive ? colors.bgTertiary : colors.bgSecondary,
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                          userSelect: 'none',
                          boxShadow: isActive ? `0 0 0 1px ${colors.bgDark}` : 'none',
                        }}
                      >
                        <div style={{ fontSize: '28px', marginBottom: '6px', lineHeight: 1 }}>{restaurant.icon}</div>
                        <Text strong style={{ display: 'block', fontSize: '15px', color: colors.textPrimary }}>
                          {restaurant.label}
                        </Text>
                        <Text style={{ fontSize: '11px', color: colors.textMuted }}>
                          {restaurant.subtitle}
                        </Text>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              {/* Step 2: Menu quantity — visible only after restaurant selected */}
              {selectedRestaurant && (
                <>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '16px', fontSize: typography.fontSize.sm }}>
                    Step 2: Select the quantity for each menu
                  </Text>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                    {visibleMenus.map(menuName => {
                      const instances = selectedMenus[menuName] || [];
                      const quantity = instances.length;
                      return (
                        <div key={menuName} style={{ ...styles.menuCard, ...(quantity > 0 ? styles.menuCardActive : {}) }}>
                          <Text strong style={{ display: 'block', marginBottom: '8px' }}>{menuName}</Text>
                          <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: '12px' }}>{MENUS[menuName].price}</Text>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <Button size="small" onClick={() => { if (quantity > 0) handleRemoveMenuInstance(menuName, instances[instances.length - 1].id); }} disabled={quantity === 0} style={{ borderRadius: radius.sm }}>-</Button>
                            <Tag style={{ margin: 0, minWidth: '40px', textAlign: 'center', background: quantity > 0 ? colors.bgDark : colors.bgTertiary, color: quantity > 0 ? colors.textInverse : colors.textSecondary, border: 'none' }}>{quantity}</Tag>
                            <Button size="small" type={quantity === 0 ? 'primary' : 'default'} onClick={() => handleAddMenuInstance(menuName)} style={{ borderRadius: radius.sm, ...(quantity === 0 ? { background: colors.bgDark, borderColor: colors.bgDark } : {}) }}>+</Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {getTotalPeopleCount() > 0 && (
                <>
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Text strong>Step 3: Customize choices for each person</Text>
                    <Button size="small" icon={viewMode === 'compact' ? <EyeOutlined /> : <EyeInvisibleOutlined />} onClick={() => setViewMode(viewMode === 'compact' ? 'detailed' : 'compact')} style={styles.secondaryBtn}>
                      {viewMode === 'compact' ? 'Detailed view' : 'Compact view'}
                    </Button>
                  </div>

                  {Object.keys(MENUS).map(menuName => {
                    const instances = selectedMenus[menuName] || [];
                    if (instances.length === 0) return null;

                    return (
                      <div key={menuName} style={{ marginBottom: '16px', border: `1px solid ${colors.borderLight}`, borderRadius: radius.xl, overflow: 'hidden' }}>
                        <div style={{ padding: spacing.lg, borderBottom: `1px solid ${colors.borderSubtle}`, background: colors.bgTertiary }}>
                          <Text strong>{menuName}</Text>
                          <Text type="secondary" style={{ marginLeft: '8px' }}>({MENUS[menuName].price})</Text>
                          <Tag style={{ marginLeft: '8px', background: colors.bgDark, color: colors.textInverse, border: 'none' }}>
                            {instances.length} {instances.length > 1 ? 'people' : 'person'}
                          </Tag>
                        </div>

                        <div style={{ padding: spacing.lg }}>
                          {instances.map((instance, index) => {
                            const isExpanded = isInstanceExpanded(menuName, instance.id);
                            const isComplete = isInstanceComplete(menuName, instance);
                            const summary = getInstanceSummary(menuName, instance);
                            const quantity = instance.quantity || 1;

                            return (
                              <div key={instance.id} style={{ ...styles.variationCard, borderLeft: isComplete ? `3px solid ${colors.accentSuccess}` : `3px solid ${colors.borderMedium}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: viewMode === 'compact' ? 'pointer' : 'default' }} onClick={() => viewMode === 'compact' && toggleInstanceExpansion(menuName, instance.id)}>
                                    {viewMode === 'compact' && <DownOutlined style={{ fontSize: '12px', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
                                    <Text strong>Variation {index + 1}</Text>
                                    {isComplete ? <Tag color="success" style={{ fontSize: '11px' }}>Complete</Tag> : <Tag style={{ fontSize: '11px', background: colors.accentOrangeBg, color: colors.accentOrange, border: 'none' }}>{summary}</Tag>}
                                  </div>
                                  <Space size="small">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <Button size="small" onClick={() => handleQuantityChange(menuName, instance.id, quantity - 1)} disabled={quantity <= 1} style={{ borderRadius: radius.sm }}>-</Button>
                                      <Tag style={{ margin: 0, minWidth: '35px', textAlign: 'center', background: colors.bgDark, color: colors.textInverse, border: 'none' }}>{quantity}</Tag>
                                      <Button size="small" onClick={() => handleQuantityChange(menuName, instance.id, quantity + 1)} style={{ borderRadius: radius.sm }}>+</Button>
                                    </div>
                                    {instances.length > 1 && index > 0 && (
                                      <Tooltip title="Copy from Variation 1">
                                        <Button size="small" icon={<CopyOutlined />} onClick={(e) => { e.stopPropagation(); handleCopySelections(menuName, instances[0].id, instance.id); }} style={{ borderRadius: radius.sm }} />
                                      </Tooltip>
                                    )}
                                    <Button danger size="small" onClick={() => handleRemoveMenuInstance(menuName, instance.id)} style={{ borderRadius: radius.sm }}>✕</Button>
                                  </Space>
                                </div>

                                {(viewMode === 'detailed' || isExpanded) && (
                                  <div>
                                    {MENUS[menuName].sections.map(section => (
                                      <div key={section.name} style={{ marginBottom: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                                          <Text strong style={{ fontSize: '12px' }}>{section.name}</Text>
                                          <Tag style={{ fontSize: '10px', background: section.type === 'choice' ? colors.bgTertiary : colors.accentSuccessBg, color: section.type === 'choice' ? colors.textSecondary : colors.accentSuccess, border: 'none' }}>
                                            {section.type === 'choice' ? 'Choose 1' : 'Included'}
                                          </Tag>
                                        </div>
                                        <Row gutter={[6, 6]}>
                                          {section.items.map(item => {
                                            const isSelected = instance.selections[section.name]?.id === item.id;
                                            const isAutoIncluded = section.type === 'single';
                                            return (
                                              <Col xs={24} sm={12} key={item.id}>
                                                <div onClick={() => section.type === 'choice' && handleItemSelect(menuName, instance.id, section.name, item)} style={{ ...styles.itemCard, ...(isSelected ? styles.itemCardSelected : {}), ...(isAutoIncluded ? styles.itemCardIncluded : {}), cursor: section.type === 'choice' ? 'pointer' : 'default' }}>
                                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                                    <div style={{ flexGrow: 1 }}>
                                                      <Text strong style={{ fontSize: '12px' }}>{item.name}</Text>
                                                      {item.nameEn && <div><Text type="secondary" style={{ fontSize: '10px' }}>{item.nameEn}</Text></div>}
                                                    </div>
                                                    {(isSelected || isAutoIncluded) && <CheckCircleOutlined style={{ color: isSelected ? colors.bgDark : colors.accentSuccess, fontSize: '14px' }} />}
                                                  </div>
                                                </div>
                                              </Col>
                                            );
                                          })}
                                        </Row>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              <Divider />

              <div style={{ backgroundColor: colors.bgTertiary, padding: '16px', borderRadius: radius.lg, marginBottom: '16px' }}>
                <Text strong style={{ display: 'block', marginBottom: '12px' }}>Additional items (optional)</Text>
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item name="additional_items" label="Description">
                      <TextArea rows={2} placeholder="Ex: Extra drinks, extras..." size="large" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="additional_price" label="Extra Price">
                      <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" size="large" addonAfter="€" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.document_type !== currentValues.document_type}>
                {({ getFieldValue }) => getFieldValue('document_type') === 'Facture' ? (
                  <Form.Item name="invoice_number" label="Invoice Number" rules={[{ required: true, message: 'Requis pour une facture' }]}>
                    <Input size="large" placeholder="INV-2024-001" />
                  </Form.Item>
                ) : null}
              </Form.Item>

              <Form.Item name="reservation_details" label="Reservation Details (auto-filled)">
                <TextArea rows={8} placeholder="Select menu items above..." size="large" readOnly style={{ backgroundColor: colors.bgTertiary }} />
              </Form.Item>

              <Divider />

              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button onClick={() => navigate('/document-list')} size="large" style={styles.secondaryBtn}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={loading} icon={<FileTextOutlined />} size="large" style={styles.primaryBtn}>Generate Document</Button>
              </Space>
            </Form>

            <Divider />

            <div style={styles.infoBox}>
              <Text strong style={{ display: 'block', marginBottom: spacing.sm }}>Document Types</Text>
              <Text style={{ fontSize: typography.fontSize.sm, color: colors.textTertiary }}>
                • <strong>Contract</strong>: Initial reservation document<br />
                • <strong>Confirmation</strong>: Confirmation after payment<br />
                • <strong>Invoice</strong>: Billing document (requires invoice number)
              </Text>
            </div>
          </Space>
        </div>
      </div>
    </div>
  );
}

export default LunchDocumentGenerator;
